import { Node, Range, Transforms } from 'slate';
import { ELEMENT_PARAGRAPH, ELEMENT_TABLE } from '@udecode/plate';

import analytics from 'scripts/lib/analytics';
import deserializeHtml from 'components/text_editor_new/lib/deserialize_html';
import { hasPluginWithKey } from 'components/text_editor_new/lib/has_plugin_with_key';
import { removeExtraBreaks } from 'components/text_editor_new/lib/remove_extra_breaks';
import { removeAppleLineBreaks } from 'components/text_editor_new/lib/remove_apple_linebreaks';
import sanitize from 'components/text_editor_new/lib/sanitize_html';
import { stripZeroWidthCharacters } from 'components/text_editor_new/lib/strip_zero_width_characters';

// `addTrailingParagraphAfterTable` gives the agent a line to type into when a pasted table lands as
// the last block. Only composers that trim trailing empty paragraphs on save (note, task) opt in — the
// paragraph is stripped on submit so nothing leaks. Email doesn't trim, so it stays off there.
export default function withPaste(editor, { addTrailingParagraphAfterTable = false } = {}) {
  const { insertData } = editor;

  editor.insertData = data => {
    // Send the analytics event to collect the original data types. Include `editorId` as it sometimes may give a hint
    // as to where the agent is trying to paste to
    analytics.track('Text Editor Content Pasted', {
      dataTypes: [...data.types],
      editorId: editor.id,
    });

    // HACK: For some reason, pasting plaintext into the editor doesn't seem to do the expected thing of
    // first deleting the fragment that's currently selected before inserting the text. This means that
    // if you have a variable selected, pasting plaintext (e.g. copied text from an input elsewhere
    // in the app) will modify the contents of the variable, rather than replacing them.
    if (data.getData('application/x-slate-fragment-old')) {
      const oldSlateHtml = data.getData('application/x-slate-fragment-old');
      const fragment = editor.getSaturatedFragment(oldSlateHtml);
      Transforms.insertFragment(editor, fragment);
      return;
    } else if (data.types.includes('text/html') && !data.types.includes('application/x-slate-fragment')) {
      // If we see an HTML fragment that is not a "slate direct copy-paste fragment" (i.e. we are copying
      // from an external source or from another conversation item), we want to tweak the html to match the
      // way it was serialized - otherwise the pasted content will not match the source
      const sourceHtml = data.getData('text/html');
      const sanitized = stripZeroWidthCharacters(sanitize(sourceHtml));
      const transformedHtml = removeAppleLineBreaks(removeExtraBreaks(sanitized));

      if (transformedHtml) {
        // Tables (e.g. from Google Sheets) go through our own deserializer rather than Plate's
        // clipboard path. Plate's per-plugin paste rules don't capture cell background, font color
        // on <td>, or bold-via-font-weight, and its table insertFragment drops to plaintext when the
        // caret is on a non-empty line. Our deserializer captures the formatting (matching the
        // save/reload path) and we control the insertion.
        if (hasPluginWithKey(editor, 'table') && /<table[\s>]/i.test(transformedHtml)) {
          return insertTableFragment(editor, transformedHtml, insertData, { addTrailingParagraphAfterTable });
        }

        const updatedDataTransfer = new DataTransfer();
        updatedDataTransfer.setData('text/html', transformedHtml);
        return insertData(updatedDataTransfer);
      }
    } else if (data.getData('text/plain')) {
      editor.deleteFragment();
    }

    insertData(data);
  };

  return editor;
}

// Deserialize pasted table HTML with our own deserializer (preserving cell background, font color and
// bold) and insert it as new top-level blocks after the current line. We use insertNodes rather than
// insertFragment because insertFragment flattens a block-level table into the caret's paragraph
// (dropping the table structure and, on a non-empty line, degrading to plaintext).
function insertTableFragment(editor, html, insertData, { addTrailingParagraphAfterTable }) {
  const fragment = deserializeHtml(html, editor);
  if (!fragment || !fragment.length) {
    const dataTransfer = new DataTransfer();
    dataTransfer.setData('text/html', html);
    return insertData(dataTransfer);
  }

  if (editor.selection && Range.isExpanded(editor.selection)) {
    Transforms.delete(editor);
  }

  Transforms.insertNodes(editor, fragment, { at: tableInsertPath(editor), select: true });

  // A table pasted as the last block leaves no line to click into below it. When opted in, append an
  // empty paragraph so there's somewhere to type. It's a one-shot insert (not a normalizer/plugin), so
  // it can't re-trigger the normalization loop that the old trailing-block plugin caused.
  if (addTrailingParagraphAfterTable && isTableLastBlock(editor)) {
    Transforms.insertNodes(editor, emptyParagraph(), { at: [editor.children.length] });
  }
}

function isTableLastBlock(editor) {
  const lastBlock = editor.children[editor.children.length - 1];
  return !!lastBlock && lastBlock.type === ELEMENT_TABLE;
}

function emptyParagraph() {
  return { type: ELEMENT_PARAGRAPH, children: [{ text: '' }] };
}

// Where to drop a pasted table. Normally it goes on a new line after the caret's block, but if the
// caret is on an empty line (e.g. a blank note) we drop it onto that line instead — otherwise the
// table would leave a stray empty paragraph above it.
function tableInsertPath(editor) {
  const { selection } = editor;
  if (!selection) {
    return [editor.children.length];
  }
  const index = selection.focus.path[0];
  const block = editor.children[index];
  const isEmptyLine = block && Node.string(block) === '';
  return isEmptyLine ? [index] : [index + 1];
}
